import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import LegalPageLayout from '../components/LegalPageLayout';
import { LEGAL } from '../lib/legalConstants';

interface DeletionStatusResponse {
  confirmation_code: string;
  status: 'completed' | 'not_found' | 'error' | 'unknown';
  message: string;
  provider?: string;
  created_at?: string;
}

const STATUS_LABELS: Record<string, string> = {
  completed: 'Traitement effectué',
  not_found: 'Aucune donnée OAuth trouvée',
  error: 'Erreur technique',
  unknown: 'Statut indisponible',
};

export default function DataDeletionStatus() {
  const [searchParams] = useSearchParams();
  const code = (searchParams.get('code') || '').trim().toUpperCase();
  const [loading, setLoading] = useState(Boolean(code));
  const [result, setResult] = useState<DeletionStatusResponse | null>(null);

  useEffect(() => {
    if (!code) return;

    let cancelled = false;
    setLoading(true);

    fetch(`/api/auth/oauth/deletion-status/${encodeURIComponent(code)}`)
      .then(async (res) => {
        const data = (await res.json()) as DeletionStatusResponse;
        if (!cancelled) setResult(data);
      })
      .catch(() => {
        if (!cancelled) {
          setResult({
            confirmation_code: code,
            status: 'unknown',
            message: `Impossible de récupérer le statut. Contactez ${LEGAL.contactEmail} en indiquant le code ${code}.`,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  return (
    <LegalPageLayout
      title="Suppression de vos données"
      subtitle={`${LEGAL.appName} — suivi de demande`}
    >
      {!code && (
        <>
          <p>
            Cette page affiche le statut d&apos;une demande de suppression de données liées
            à une connexion OAuth (Facebook, Google…), notamment lorsque vous retirez
            l&apos;autorisation accordée à {LEGAL.appName} depuis les paramètres du
            fournisseur.
          </p>
          <p>
            Si Facebook vous a redirigé ici, le code de confirmation figure dans
            l&apos;adresse de la page (<code>?code=…</code>).
          </p>
          <p>
            Pour les instructions complètes, consultez la section{' '}
            <Link to="/confidentialite#suppression-donnees">
              Suppression de vos données
            </Link>{' '}
            de notre politique de confidentialité.
          </p>
        </>
      )}

      {code && loading && <p className="legal-meta">Chargement du statut…</p>}

      {code && !loading && result && (
        <>
          <p className="legal-meta">
            Code de confirmation : <strong>{result.confirmation_code}</strong>
          </p>
          <p>
            <strong>{STATUS_LABELS[result.status] || result.status}</strong>
          </p>
          <p>{result.message}</p>
          {result.created_at && (
            <p className="legal-meta">
              Horodatage : {new Date(result.created_at).toLocaleString('fr-FR')}
            </p>
          )}
        </>
      )}

      <p>
        La suppression automatique via Facebook retire uniquement la{' '}
        <strong>liaison OAuth</strong> (identifiant Facebook, e-mail et tokens associés).
        Elle ne supprime pas votre compte joueur Webtarot, vos parties ni vos messages.
        Pour une <strong>suppression complète du compte</strong>, écrivez à{' '}
        <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a> en précisant
        votre pseudo.
      </p>

      <p>
        <Link to="/confidentialite#suppression-donnees">← Retour aux instructions</Link>
      </p>
    </LegalPageLayout>
  );
}
